import {
  AnnotationShape,
  LogoShape,
  TextShape,
  Text,
} from '@/web-components/import-wizard/typings/pintura';
import axios from 'axios';
import { textShapes, useStore as useShapeStore } from '@/stores/shape';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useActionStore } from '@/stores/action';
import { useStore as useMirrorStore } from '@/stores/mirror';
import { useStore as useNotificationStore } from '@/stores/notification';
import { useStore as useColorsManagementStore } from '@/stores/colorsManagement';
import { calculatePrintArea } from './index';
import { determineRangeSAPCode } from '@/utils/generatePriceParamsEventPayload';
import { px2mm } from '@/utils';
import { format } from 'date-fns';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import {
  NewProofForm,
  NewProductPrintPositionForms,
  CategoryConfiguration,
  LanyardConfiguration,
  RegularConfiguration,
  PmsColor,
  ProductPrintPosition,
  PrintTechnique,
  Category,
} from '@midocean/webapps-shared-domain';

export async function convertToBlob(fileUrl: string): Promise<Blob> {
  const res = await axios({
    url: fileUrl,
    method: 'GET',
    responseType: 'blob',
  });
  return res.data;
}

export async function getArtworkFiles(
  shapes: AnnotationShape[]
): Promise<File[]> {
  const logoShapes: LogoShape[] = shapes.filter(
    (x) => x.kind === 'Logo'
  ) as LogoShape[];

  return await Promise.all(
    logoShapes.map(async (artwork) => {
      const imageBlob =
        artwork.variantArtworkImages && artwork.variantArtworkImages.length > 0
          ? artwork.pngFileWithoutConverters
          : artwork.pngFile;
      return new File([imageBlob], artwork.personalizationArtworkId, {
        type: 'image/png',
      });
    })
  );
}

export function getSmallDetailsFiles(shapes: AnnotationShape[]): File[] {
  const files: File[] = [];
  const logoShapes: LogoShape[] = shapes.filter(
    (x) => x.kind === 'Logo'
  ) as LogoShape[];

  const textShapes: TextShape[] = shapes.filter(
    (x) => x.kind === 'Text'
  ) as TextShape[];

  logoShapes.map((a) => {
    if (a.smallDetailsResult) {
      files.push(
        new File(
          [a.smallDetailsResult.pngFileBlob],
          a.personalizationArtworkId,
          {
            type: 'image/png',
          }
        )
      );
    }
  });

  textShapes.map((t) => {
    if (t.smallDetailsResult) {
      files.push(
        new File([t.smallDetailsResult.pngFileBlob], t.id, {
          type: 'image/png',
        })
      );
    }
  });

  return files;
}

export function getTextFiles(shapes: AnnotationShape[]): File[] {
  const files: File[] = [];
  const textShapes: TextShape[] = shapes.filter(
    (x) => x.kind === 'Text'
  ) as TextShape[];

  textShapes.map((t) => {
    files.push(
      new File([t.pngFileWithoutConverters], t.id, {
        type: 'image/png',
      })
    );
  });

  return files;
}

export function getVariantFiles(shapes: AnnotationShape[]): File[] {
  const variantFiles: File[] = [];

  shapes.forEach((artwork) => {
    if (artwork.variantArtworkImages) {
      artwork.variantArtworkImages.forEach((previewImage) => {
        variantFiles.push(
          new File(
            [previewImage.variantArtworkPng],
            artwork.id + '@' + previewImage.variantColor,
            {
              type: 'image/png',
            }
          )
        );
      });
    }
  });
  return variantFiles;
}

export const getPositionImageDPI = (
  positionId: string,
  productPrintPositions: ProductPrintPosition[]
): number => {
  return productPrintPositions?.find((p) => p.id === positionId)
    ?.originalImageWebDpi as number;
};

export const getPrintTechniqueSapCode = (
  pId: string,
  tId: string,
  productPrintPositions: ProductPrintPosition[]
): string => {
  return productPrintPositions
    .filter((p) => p.id === pId)
    .map(({ printTechniques }) =>
      printTechniques?.find((pt) => pt.id === tId)
    )[0]?.printTechnique?.code as string;
};

export const getPrintTechnique = (
  pId: string,
  tId: string,
  productPrintPositions: ProductPrintPosition[]
): PrintTechnique | undefined => {
  return productPrintPositions
    .filter((p) => p.id === pId)
    .map(({ printTechniques }) =>
      printTechniques?.find((t) => t.id === tId)
    )[0];
};

export const getConvertedColorToPms = (
  colorName: string,
  availablePmsColors: PmsColor[]
): PmsColor | undefined => {
  return availablePmsColors?.find((color) => {
    return color.name === colorName;
  });
};

export const logosByPosition = (
  positionId: string,
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[]
): LogoShape[] =>
  shapes.filter((shape) =>
    positionShapeMap.some(
      (psm) =>
        psm.id === positionId &&
        psm.logoId === shape.id &&
        shape.kind === 'Logo'
    )
  ) as LogoShape[];

export const textsByPosition = (
  positionId: string,
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[]
): TextShape[] =>
  shapes
    .filter((shape) =>
      positionShapeMap.some(
        (psm) => psm.id === positionId && psm.logoId === shape.id
      )
    )
    .filter(textShapes) as TextShape[];

export const convertedColorToPms = (text: Text): PmsColor => {
  return {
    name: text.colorName,
    originalName: text.colorName,
    red: text.color[0],
    green: text.color[1],
    blue: text.color[2],
    colorFuzz: 4,
    isOriginalSpotColor: false,
  } as PmsColor;
};

export const mapProductCategoryConfiguration = (
  productPrintPosition: ProductPrintPosition
): CategoryConfiguration => {
  const mirrorStore = useMirrorStore();

  switch (productPrintPosition.category) {
    case Category.Lanyard: {
      const mirrorSettings = mirrorStore.positionMirrorSettings.find(
        (x) => x.positionId === productPrintPosition.id
      );
      return {
        $type:
          'Customers.Domain.Models.LanayrdPrintPositionCategoryConfiguration, Customers.Domain',
        isMirrorApplied: mirrorSettings?.isMirrorApplied,
        isOppositeRotationApplied: mirrorSettings?.isOppositeRotationApplied,
      } as LanyardConfiguration;
    }
    default: {
      return {
        $type:
          'Customers.Domain.Models.RegularPrintPositionCategoryConfiguration, Customers.Domain',
      } as RegularConfiguration;
    }
  }
};

export function getProofFormFromState(isPdfPreview: boolean): NewProofForm {
  const productStore = useProductStore();
  const shapeStore = useShapeStore();

  for (let i = 0; i < shapeStore.shapes.length; i++) {
    shapeStore.shapes[i].zindex = i;
  }

  const actionStore = useActionStore();
  const colorsManagementStore = useColorsManagementStore();

  const newProofForm: NewProofForm = {
    newProductPrintPositionForms: productStore.unavailablePrintPositions.map(
      (u) => {
        const productPrintPositions = productStore.printPositions;
        const dpi = getPositionImageDPI(u.positionId, productPrintPositions);
        const positions = shapeStore.positionShapeMap.filter(
          (position: Record<string, string>) => {
            return position.id === u.positionId;
          }
        );

        const { width, height } = calculatePrintArea(
          dpi,
          positions,
          shapeStore.shapes
        );

        const printTechnique = getPrintTechnique(
          u.positionId,
          u.technique.id,
          productPrintPositions
        ) as PrintTechnique;

        const productPrintPosition = productStore.printPositions.find(
          (x) => x.id === u.positionId
        );

        return {
          name: u.positionName,
          SAPCode: u.positionName,
          printSizeWidthMm: width,
          printSizeHeightMm: height,
          category: productPrintPosition?.category ?? Category.Regular,
          categoryConfiguration: productPrintPosition
            ? mapProductCategoryConfiguration(productPrintPosition)
            : null,
          productPrintTechnique: {
            name: u.technique.techniqueName,
            SAPCode: getPrintTechniqueSapCode(
              u.positionId,
              u.technique.id,
              productPrintPositions
            ),
            SAPRangeCode: determineRangeSAPCode(width, height, printTechnique),
          },
          personalizationTexts: textsByPosition(
            u.positionId,
            shapeStore.shapes,
            shapeStore.positionShapeMap
          ).map((text) => {
            const angle = ((text.rotation as number) * 180) / Math.PI;

            return {
              id: text.id,
              zIndex: text.zindex,
              distanceFromTop: px2mm(text.y as number, dpi),
              distanceFromLeft: px2mm(text.x as number, dpi),
              width: px2mm(text.width as number, dpi),
              height: px2mm(text.height as number, dpi),
              rotationAngle: angle.toFixed(),
              font: text.w2pText.fontFamily,
              fontSize: text.w2pText.fontSizePT,
              bold: text.w2pText.fontWeight === 'bold',
              italic: text.w2pText.fontStyle === 'italic',
              underline: false,
              text: text.w2pText.text,
              textHtml: text.w2pText.textHtml,
              textAlignement: text.w2pText.textAlign,
              pmsColorInfo: convertedColorToPms(text.w2pText),
              originalWidthMM: text.originalWidthMM,
              originalHeightMM: text.originalHeightMM,
              variantArtworkImages: text.variantArtworkImages,
              smallDetails:
                DoSmallDetailsPart &&
                text &&
                text.smallDetailsResult &&
                text.smallDetailsResult.status !== 'NoCheck'
                  ? {
                      positiveLoss: text.smallDetailsResult.positiveDetailsLoss,
                      negativeLoss: text.smallDetailsResult.negativeDetailsLoss,
                      status: text.smallDetailsResult.status,
                      calculatedQuality:
                        text.smallDetailsResult.calculatedQuality,
                      approvedAt:
                        !isPdfPreview && text.smallDetailsResult.approvedAt
                          ? getUtcDateTime(text.smallDetailsResult.approvedAt)
                          : null,
                    }
                  : null,
            };
          }),
          personalizationArtworks: logosByPosition(
            u.positionId,
            shapeStore.shapes,
            shapeStore.positionShapeMap
          ).map((artwork) => {
            const shape = shapeStore.shapes?.find((x) => x.id === artwork.id);
            const angle = ((shape?.rotation as number) * 180) / Math.PI;
            return {
              id: artwork.personalizationArtworkId,
              zIndex: artwork.zindex,
              isRoundLogo: artwork.isRoundLogo,
              widthWithCropping: px2mm(shape?.width as number, dpi),
              heightWithCropping: px2mm(shape?.height as number, dpi),
              pmsColorsInPngFile: artwork.pmsColorsInPngFile,
              originalWidthMM: artwork.originalWidthMM,
              originalHeightMM: artwork.originalHeightMM,
              originalVerticalResolutionDPIs:
                artwork.originalVerticalResolutionDPIs,
              originalHorizontalResolutionDPIs:
                artwork.originalHorizontalResolutionDPIs,
              pngHorizontalResolutionDPIs:
                artwork.pngHorizontalResolutionDPIs.toFixed(), // NOTE: Recalculate DPIs using formula
              pngVerticalResolutionDPIs:
                artwork.pngVerticalResolutionDPIs.toFixed(), // NOTE: Recalculate DPIs using formula
              pngFileUrl: artwork.pngFileUrl,
              pngWidthPixels: artwork.pngWidthPixels,
              pngHeightPixels: artwork.pngHeightPixels,
              distanceFromTopWithCropping: px2mm(shape?.y as number, dpi),
              distanceFromLeftWithCropping: px2mm(shape?.x as number, dpi),
              rotationAngle: angle.toFixed(),
              title: artwork.title,
              fileName: artwork.fileName,
              isVectorFileTrueVectorFile: artwork.isVectorFileTrueVectorFile,
              originalFileUrl: artwork.originalFileUrl,

              userActions: actionStore.prepareActionsPayload(
                artwork.personalizationArtworkId
              ),
              smallDetails:
                DoSmallDetailsPart &&
                shape &&
                shape.smallDetailsResult &&
                shape.smallDetailsResult.status !== 'NoCheck'
                  ? {
                      positiveLoss:
                        shape.smallDetailsResult.positiveDetailsLoss,
                      negativeLoss:
                        shape.smallDetailsResult.negativeDetailsLoss,
                      status: shape.smallDetailsResult.status,
                      calculatedQuality:
                        shape.smallDetailsResult.calculatedQuality,
                      approvedAt:
                        !isPdfPreview && shape.smallDetailsResult.approvedAt
                          ? getUtcDateTime(shape.smallDetailsResult.approvedAt)
                          : null,
                    }
                  : null,
            };
          }),
        } as unknown as NewProductPrintPositionForms;
      }
    ),
  };

  return newProofForm;
}

export function getUtcDateTime(dateString: string): Date {
  return new Date(
    format(new Date(dateString.slice(0, -1)), 'yyyy-MM-dd HH:mm:ss')
  );
}

export function isShapeColorChanged(): boolean | undefined {
  const shapeStore = useShapeStore();
  const productStore = useProductStore();
  const colorsManagementStore = useColorsManagementStore();
  const posShapes = shapeStore.positionShapeMap.filter(
    (element) => element.id === productStore.activePosition.id
  );
  let shapeColorElement: PmsColor | undefined;
  //let isAvailablePmsColor = true;

  shapeStore.shapes.forEach((shape: AnnotationShape) => {
    if (
      shape.kind === 'Logo' &&
      posShapes.find((element) => element.logoId === shape.id)
    ) {
      shapeColorElement = shape.pmsColorsInPngFile.find(
        (color: PmsColor) =>
          color.name !== color.originalName && color.originalName !== undefined
      );
    }
  });

  // if (shapeColorElement) {
  //   isAvailablePmsColor =
  //     Object.keys(
  //       colorsManagementStore.availablePmsColors?.find((c) => {
  //         return c.name === shapeColorElement?.originalName;
  //       }) || ({} as PmsColor)
  //     ).length !== 0;
  // }

  return shapeColorElement !== undefined; //&& !isAvailablePmsColor;
}
